import { Inject, inject, Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, interval, Observable } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { LoginState, LoginStateService } from '@client-monorepo/applets/auth';

declare const window: any;

@Injectable({
  providedIn: 'root',
})
export class VersioningService {
  private swUpdate = inject(SwUpdate);
  private loginStateService = inject(LoginStateService);
  private checkPeriodTime = 60 * 1000;
  private router = inject(Router);
  private safeRoute: string[] = [
    '/auth/onboarding',
    '/hub',
    '/service/credit/overview',
    '/service/credit/pre-register',
    '/subscription',
    '/subscription/subscription-management',
    '/home',
  ];

  constructor(@Inject('APP_ENV') private environment: { [key: string]: string }) {
    if (parseInt(this.environment['version_check_period_seconds'])) {
      this.checkPeriodTime = parseInt(this.environment['version_check_period_seconds']) * 1000;
    }
  }

  public run() {
    if (!this.swUpdate.isEnabled) {
      console.warn('SERVICE WORKER IS NOT ENABLED');
      return;
    }
    this.invoke();
    this.checkForUpdate();
  }

  invoke() {
    try {
      this.swUpdate.versionUpdates.pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')).subscribe(() => {
        this.checkRouteIsSafe().then(() => {
          this.reloadPage();
        });
      });
    } catch (e) {
      console.error('versioning service: ', e);
    }
  }

  checkForUpdate(): void {
    const everyPeriodTime = interval(this.checkPeriodTime);
    everyPeriodTime.subscribe(async () => {
      try {
        const updateFound = await this.swUpdate.checkForUpdate();
        console.log(updateFound ? 'A new version is available.' : 'Already on the latest version.');
      } catch (err) {
        console.error('Failed to check for updates:', err);
      }
    });
  }

  hasNewVersion(): Observable<boolean> {
    return new Observable((subscriber) => {
      this.swUpdate
        .checkForUpdate()
        .then((updateFound) => {
          subscriber.next(updateFound);
          subscriber.complete();
        })
        .catch((err) => {
          console.error('Failed to check for updates:', err);
          subscriber.error(err);
          subscriber.complete();
        });
    });
  }
  private isLoginRoute(): boolean {
    const currentUrl = window.location.pathname;
    const loginRoute = ['/auth/login'];
    return loginRoute.indexOf(currentUrl) >= 0;
  }
  private checkRouteIsSafe(): Promise<boolean> {
    return new Promise((resolve) => {
      if (this.loginStateService.state() === LoginState.PHONENUMBER && this.isLoginRoute()) {
        resolve(true);
      }
      const currentUrl = window.location.pathname;
      if (this.safeRoute.indexOf(currentUrl) >= 0) {
        resolve(true);
      }
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          const endUrl = this.router.url;
          if (!this.safeRoute.includes(endUrl)) {
            return;
          }
          resolve(true);
        }
      });
    });
  }
  reloadPage(): void {
    if (!window.activeRequests || window.lastApiCallTime < new Date().getTime() - 60 * 1000) {
      document.location.reload();
      return;
    }
    setTimeout(() => {
      this.reloadPage();
    }, 300);
  }
}
